import { useEffect, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthz } from '@/features/auth/hooks/useAuthz'
import NoAccessPage from '@/features/auth/pages/NoAccessPage'
import ErrorState from '@/shared/components/feedback/ErrorState'
import LoadingSpinner from '@/shared/components/ui/LoadingSpinner'

const ROUTE_REFETCH_MS = 60_000

/** Resolves `/api/me` once for the signed-in tree; routes below can trust `can()`. */
export default function AuthGate({ children }: { children: ReactNode }) {
  const { me, isLoading, noAccess, error, refetch, dataUpdatedAt } = useAuthz()
  const { pathname } = useLocation()

  // Re-read permissions on navigation (AUTH §3), at most once a minute.
  useEffect(() => {
    if (dataUpdatedAt && Date.now() - dataUpdatedAt > ROUTE_REFETCH_MS) refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only navigation should trigger this.
  }, [pathname])

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 bg-surface">
        <LoadingSpinner size="lg" />
        <p className="text-sm text-gray-500">Signing you in…</p>
      </div>
    )
  }

  if (noAccess) return <NoAccessPage />

  // Keep showing the app on a background refetch failure; only block when there's no data at all.
  if (!me) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-surface">
        <ErrorState title="Couldn't load your access." error={error} onRetry={() => refetch()} />
      </div>
    )
  }

  return <>{children}</>
}
